import React from 'react';
import { Button } from './ui/button';
import ScoreBreakdown from './ScoreBreakdown';
import { CheckCircle, AlertTriangle, XCircle, MessageSquare, MapPin, Loader2, Star, Plus, Check, Bookmark, BookmarkCheck } from 'lucide-react';

// Maps backend canonical reason strings to translation keys
const WHY_MAP = {
  'Best price for your budget': 'why_best_price',
  'Fast delivery': 'why_fast_delivery',
  'Verified supplier': 'why_verified',
  'MOQ fits your order': 'why_moq_fits',
  'Low risk supplier': 'why_low_risk',
  'Competitive pricing': 'why_competitive',
  'High capacity supplier': 'why_high_capacity',
};

// Client-side fallback: derive 2-3 reasons from supplier scoring data
// when backend doesn't provide why_reasons (older saved searches).
const deriveReasons = (s) => {
  const reasons = [];
  const sb = s.score_breakdown || {};
  if (sb.price >= 85 || sb.price === 100) reasons.push('Best price for your budget');
  if (sb.delivery >= 85 || (s.lead_time_days && s.lead_time_days <= 14)) reasons.push('Fast delivery');
  if (s.verified) reasons.push('Verified supplier');
  if (sb.moq === 100 || (s.moq && s.moq <= 100)) reasons.push('MOQ fits your order');
  if (s.risk_level === 'Low' || sb.reliability >= 80) reasons.push('Low risk supplier');
  if (reasons.length < 2) reasons.push('Competitive pricing');
  if (reasons.length < 2) reasons.push('High capacity supplier');
  return reasons.slice(0, 3);
};

const RiskBadge = ({ riskLevel, t }) => {
  const config = {
    Low:    { icon: CheckCircle, iconClass: 'text-emerald-600', badgeClass: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: t('lowRisk') },
    Medium: { icon: AlertTriangle, iconClass: 'text-amber-600', badgeClass: 'bg-amber-100 text-amber-700 border-amber-200', label: t('mediumRisk') },
    High:   { icon: XCircle, iconClass: 'text-rose-600', badgeClass: 'bg-rose-100 text-rose-700 border-rose-200', label: t('highRisk') },
  };
  const c = config[riskLevel] || config.Medium;
  const Icon = c.icon;
  return (
    <div className="flex items-center gap-2">
      <Icon className={`w-4 h-4 ${c.iconClass}`} strokeWidth={2} />
      <span className={`text-xs px-2 py-1 rounded-full border font-medium ${c.badgeClass}`} data-testid="risk-badge">{c.label}</span>
    </div>
  );
};

const SupplierCard = ({ supplier, index, t, onGenerateMessage, isSelected, canSelect, onToggleSelect, isSaved, isSaving, onToggleSave }) => {
  const isBest = index === 0;
  const rawReasons = (supplier.why_reasons && supplier.why_reasons.length >= 2)
    ? supplier.why_reasons
    : (supplier.why_reasons && supplier.why_reasons.length === 1)
      ? [...supplier.why_reasons, ...deriveReasons(supplier).filter((r) => r !== supplier.why_reasons[0])].slice(0, 3)
      : deriveReasons(supplier);
  const reasonTexts = rawReasons.map((r) => (WHY_MAP[r] ? t(WHY_MAP[r]) : r));
  const hasRange = supplier.price_range_low && supplier.price_range_high;
  const totalScore = supplier.score_breakdown?.total ?? supplier.reliability_score ?? 0;
  const scoreBadgeColor = totalScore >= 75
    ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
    : totalScore >= 55
      ? 'bg-amber-100 text-amber-700 border-amber-200'
      : 'bg-rose-100 text-rose-700 border-rose-200';

  return (
    <div
      className={`relative rounded-lg p-6 transition-all duration-200 ${isBest ? 'bg-blue-50 border-2 border-blue-500' : 'bg-white border border-slate-200'}`}
      data-testid={isBest ? 'best-supplier-card' : 'supplier-card'}
    >
      {isBest && (
        <div className="absolute -top-3 left-4 bg-blue-600 text-white text-xs px-3 py-1 rounded-full font-medium" data-testid="best-match-badge">{t('bestMatchBadge')}</div>
      )}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex-1">
          {/* Supplier Header */}
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="text-lg font-bold text-slate-900">{supplier.name}</h3>
                {supplier.verified && (
                  <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium" data-testid="verified-badge">{t('verified')}</span>
                )}
                <span
                  className={`text-xs px-2.5 py-0.5 rounded-full font-bold border ${scoreBadgeColor}`}
                  data-testid="score-badge"
                >
                  {t('scoreOutOf')}: {totalScore}/100
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-500">
                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{supplier.location}</span>
              </div>
            </div>
          </div>

          {/* Recommended because */}
          {reasonTexts.length > 0 && (
            <div className="mb-4 px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-100" data-testid="why-supplier">
              <div className="flex items-center gap-2 mb-1.5">
                <Star className="w-4 h-4 text-amber-500 shrink-0" />
                <span className="text-sm font-medium text-slate-600">{t('recommendedBecause')}</span>
              </div>
              <ul className="space-y-1 ml-6 list-disc list-outside text-sm text-slate-700" data-testid="why-reasons-list">
                {reasonTexts.map((r, i) => (
                  <li key={r} data-testid={`why-reason-${i}`}>{r}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div>
              <span className="text-xs text-slate-500">{t('priceRange')}</span>
              <p className="font-bold text-slate-900 text-sm">
                {hasRange
                  ? `$${supplier.price_range_low} – $${supplier.price_range_high}`
                  : `$${supplier.price}`}
                <span className="text-xs font-normal text-slate-500">{t('perUnit')}</span>
              </p>
            </div>
            <div>
              <span className="text-xs text-slate-500">{t('moq')}</span>
              <p className="font-bold text-slate-900 text-sm">{supplier.moq} {t('units')}</p>
            </div>
            <div>
              <span className="text-xs text-slate-500">{t('leadTime')}</span>
              <p className="font-bold text-slate-900 text-sm">{supplier.lead_time_days} {t('days')}</p>
            </div>
            <div>
              <span className="text-xs text-slate-500">{t('risk')}</span>
              <RiskBadge riskLevel={supplier.risk_level} t={t} />
            </div>
          </div>

          {/* Score Breakdown */}
          <ScoreBreakdown breakdown={supplier.score_breakdown} t={t} />
        </div>

        <div className="shrink-0 flex flex-col gap-2 md:w-44">
          <Button
            onClick={() => onGenerateMessage(supplier)}
            className={`transition-all duration-200 ${isBest ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-700 hover:bg-slate-800'}`}
            data-testid="generate-message-button"
          >
            <MessageSquare className="w-4 h-4 mr-2" />{t('generateMessage')}
          </Button>
          <Button
            onClick={() => onToggleSelect(supplier.name)}
            variant={isSelected ? 'default' : 'outline'}
            disabled={!isSelected && !canSelect}
            className={isSelected ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
            data-testid="add-to-comparison-button"
          >
            {isSelected ? (
              <><Check className="w-4 h-4 mr-2" />{t('addedToComparison')}</>
            ) : (
              <><Plus className="w-4 h-4 mr-2" />{t('addToComparison')}</>
            )}
          </Button>
          <Button
            onClick={() => onToggleSave(supplier)}
            variant={isSaved ? 'default' : 'outline'}
            disabled={isSaving}
            className={isSaved ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
            data-testid="save-supplier-button"
          >
            {isSaving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('saving')}</>
            ) : isSaved ? (
              <><BookmarkCheck className="w-4 h-4 mr-2" />{t('savedSupplier')}</>
            ) : (
              <><Bookmark className="w-4 h-4 mr-2" />{t('saveSupplier')}</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SupplierCard;
